import { Stack, Text } from "@certn/cascadia";
import { Card } from "@certn/cascadia-react";
import { formatCurrency } from "@certn/utils";
import { css } from "@emotion/css";
import { Fragment } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useClientAccount } from "../../hooks/useClientAccount";
import { Schemas } from "../../types";
import { useCobrandingEnabled } from "@certn/cascadia-react/context/BrandProvider";
import { useCanViewClientPortalPricing } from "../permissions/Permissions";

export interface PackageSummaryCardProps {
  id: string;
  title: string;
  price: number | null;
  checks: Schemas["BannerPackageCheck"][];
  testID: string;
  currency?: Schemas["CurrencyEnum"];
}

export function PackageSummaryCard({
  id,
  title,
  price,
  checks,
  testID = "package-summary-card",
  currency,
}: PackageSummaryCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerGroups } = useClientAccount();
  const cobrandingEnabled = useCobrandingEnabled();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const priceColor = cobrandingEnabled ? "text-base" : "text-success";

  const handleNavigation = () => {
    if (customerGroups && customerGroups.length > 0) {
      navigate(`/order?package_id=${id}&group_id=${customerGroups[0].id}`);
    }
  };

  return (
    <Card className={styles.card} testID={testID} hideBorder>
      <div
        role="button"
        tabIndex={0}
        onClick={handleNavigation}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            handleNavigation();
          }
        }}
        data-testid={`${testID}-button`}
      >
        <Stack gap="space-1" className={styles.content}>
          <Stack orientation="horizontal" justify="space-between">
            <Text
              color="text-base"
              style="global/heading-medium"
              testID={`${testID}-package-title`}
            >
              {title}
            </Text>
            <Text
              color={priceColor}
              style="global/heading-medium"
              testID={`${testID}-package-price`}
            >
              {canViewClientPortalPricing && price != null
                ? t("From {{packagePrice}}", {
                    packagePrice: formatCurrency(price, undefined, currency, 2),
                  })
                : ""}
            </Text>
          </Stack>
          <Stack gap="space-2">
            <Text style="global/info-label">
              {checks.length} <Trans>checks</Trans>
            </Text>
            <Text style="global/info-label" color="text-weak">
              {checks.map((check, index) => (
                <Fragment key={index}>
                  {index > 0 && <>&nbsp;&nbsp;•&nbsp;&nbsp;</>}
                  {check.check_name}
                </Fragment>
              ))}
            </Text>
          </Stack>
        </Stack>
      </div>
    </Card>
  );
}

const styles = {
  card: css({
    flex: 1,
    alignSelf: "stretch",
    borderRadius: "var(--border-radius-3)",
    minWidth: 260,
    maxWidth: 488,
    "&:hover": {
      boxShadow: "var(--shadow-card)",
    },
    "&[data-focus-visible]": {
      boxShadow: "var(--focus-action)",
    },
    cursor: "pointer",
  }),
  content: css({
    padding: "var(--space-4)",
  }),
};
